import { Link } from "react-router-dom";
import { Activity, CalendarCheck, Info } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { SYMPTOMS } from "@/data/content";

const Symptoms = () => (
  <>
    <SEO
      title="Common STD/STI Symptoms | Confidential Consultation in Delhi — Dr. Ankur Lal"
      description="Genital discharge, burning urination, ulcers, sores, warts, itching, rash and other STD/STI symptoms. Many STIs have no symptoms — confidential assessment in Delhi by Dr. Ankur Lal."
      path="/std-symptoms"
    />
    <PageHero
      eyebrow="STD / STI Symptoms Guide"
      title="Common STD/STI Symptoms"
      intro="If you notice any of the following, a confidential consultation with a specialist can give you clear answers and, where needed, prompt treatment."
    />

    <section className="py-14 sm:py-20" data-testid="symptoms-list">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
          {SYMPTOMS.map((s, i) => (
            <Reveal key={s} delay={(i % 6) * 0.05}>
              <div
                data-testid={`symptom-${i}`}
                className="h-full bg-white border border-slate-900/10 rounded-2xl px-5 py-4 flex items-center gap-3 hover:border-emerald-900/25 hover:shadow-md transition-all"
              >
                <Activity className="w-4 h-4 text-emerald-800 shrink-0" />
                <span className="text-sm font-medium text-slate-800 leading-snug">{s}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10">
          <div className="bg-emerald-50 border border-emerald-900/15 rounded-3xl p-7 sm:p-9 flex items-start gap-4" data-testid="symptoms-important-note">
            <Info className="w-6 h-6 text-emerald-900 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-serif text-2xl font-semibold text-slate-950">
                No symptoms doesn't mean no infection
              </h2>
              <p className="mt-3 text-slate-700 leading-relaxed">
                Many STIs may cause few or no symptoms. Testing and medical assessment may
                therefore be important even if you feel completely well — especially after
                unprotected sex or a new partner.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/booking?type=online"
                  data-testid="symptoms-book-cta"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all"
                >
                  <CalendarCheck className="w-4 h-4" /> Book a Confidential Consultation
                </Link>
                <Link
                  to="/std-hiv-testing"
                  data-testid="symptoms-testing-link"
                  className="inline-flex items-center justify-center gap-2 text-emerald-950 font-semibold text-sm px-5 py-3 underline underline-offset-8 decoration-emerald-700/40"
                >
                  STD & HIV Testing in Delhi
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaBand
      title="Noticed a Symptom? Get Clear Answers."
      text="A private consultation is the fastest way to know where you stand — and what to do next."
    />
  </>
);

export default Symptoms;
